import React, { useState, useEffect, useRef } from 'react';
import {
  X, ArrowLeft, ArrowRight, Check, Clock, Play, Pause, RotateCcw,
  SkipForward, Trophy,
} from 'lucide-react';
import { TEST_PROTOCOLS } from './testProtocols';

const API = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('prokids_token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.status === 204 ? null : res.json();
}


const COLOR_CLASSES = {
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   btn: 'bg-amber-600 hover:bg-amber-700' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    btn: 'bg-blue-600 hover:bg-blue-700' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  btn: 'bg-purple-600 hover:bg-purple-700' },
};

function TestTimer({ seconds, countdown = false, onReset }) {
  const [remaining, setRemaining] = useState(countdown ? seconds : 0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setRemaining(r => {
          if (countdown) {
            if (r <= 0) { setRunning(false); return 0; }
            return r - 1;
          }
          return r + 1;
        });
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [running, countdown]);

  useEffect(() => {
    setRemaining(countdown ? seconds : 0);
    setRunning(false);
  }, [seconds, countdown, onReset]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const alert = countdown && remaining === 0;

  return (
    <div className={`rounded-xl p-4 flex items-center justify-between ${alert ? 'bg-red-600' : 'bg-gray-900'} text-white`}>
      <div className="flex items-center gap-3">
        <Clock className="w-5 h-5 text-emerald-400" />
        <span className="text-2xl font-mono font-bold tabular-nums">{mm}:{ss}</span>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setRunning(!running)} className="bg-emerald-500 hover:bg-emerald-400 rounded-lg p-2">
          {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button onClick={() => { setRunning(false); setRemaining(countdown ? seconds : 0); }} className="bg-gray-700 hover:bg-gray-600 rounded-lg p-2">
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function EvaluationWizard({ playerId, playerName, sessionId, onClose }) {
  const [idx, setIdx] = useState(0);
  const [value, setValue] = useState('');
  const [results, setResults] = useState({}); // { type: { value, savedAt } }
  const [saving, setSaving] = useState(false);

  const test = TEST_PROTOCOLS[idx];
  const Icon = test.icon;
  const c = COLOR_CLASSES[test.color];
  const already = results[test.type];

  useEffect(() => {
    setValue('');
  }, [idx]);

  const save = async (skip = false) => {
    if (!skip && !value) return;
    if (!skip) {
      setSaving(true);
      try {
        await apiFetch('/api/v1/admin/measurements', {
          method: 'POST',
          body: JSON.stringify({
            playerId,
            sessionId,
            type: test.type,
            value: parseFloat(value),
            unit: test.unit,
          }),
        });
        setResults(r => ({ ...r, [test.type]: { value: parseFloat(value), savedAt: new Date() } }));
      } catch (e) {
        alert('Error guardando');
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    if (idx < TEST_PROTOCOLS.length - 1) setIdx(idx + 1);
  };

  const finished = Object.keys(results).length;
  const total = TEST_PROTOCOLS.length;
  const isLast = idx === total - 1;
  const isDone = finished === total || (isLast && (results[test.type] || !value));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs font-bold text-gray-500">EVALUACIÓN COMPLETA</div>
            <h3 className="font-bold text-gray-900">{playerName}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Test {idx + 1} de {total}</span>
            <span>{finished} registrados</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${((idx + 1) / total) * 100}%` }}
            />
          </div>
        </div>

        {/* Test detail */}
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className={`text-xs font-bold ${c.text}`}>{test.group}</div>
              <h4 className="text-lg font-bold text-gray-900">{test.label}</h4>
              {already && (
                <div className="mt-1 text-xs text-emerald-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Registrado: {already.value} {test.unit}
                </div>
              )}
            </div>
          </div>

          {test.equipment && (
            <div className={`${c.bg} rounded-lg p-3`}>
              <div className={`text-xs font-bold ${c.text} mb-1`}>Equipamiento</div>
              <ul className="text-sm text-gray-700 space-y-0.5">
                {test.equipment.map((e, i) => <li key={i}>• {e}</li>)}
              </ul>
            </div>
          )}

          <div>
            <div className="text-xs font-bold text-gray-500 uppercase mb-2">Protocolo</div>
            <ol className="space-y-1.5 text-sm text-gray-700">
              {test.instructions.map((ins, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-gray-400 flex-shrink-0">{i + 1}.</span>
                  <span>{ins}</span>
                </li>
              ))}
            </ol>
          </div>

          {test.tip && (
            <div className="text-xs text-gray-500 italic border-l-2 border-gray-200 pl-3">💡 {test.tip}</div>
          )}

          {test.reference && (
            <div className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
              <span className="font-bold">Referencia 8-9 años:</span> {test.reference}
            </div>
          )}

          {test.timer && (
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase mb-1">{test.timer.label}</div>
              <TestTimer seconds={test.timer.seconds} countdown={test.timer.countdown} onReset={idx} />
            </div>
          )}

          {/* Value input */}
          <div className="border-t border-gray-100 pt-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">
              Resultado {test.unit && `(${test.unit})`}
            </label>
            {test.inputType === 'scale' ? (
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setValue(String(n))}
                    className={`h-14 rounded-lg border-2 font-bold text-lg transition ${
                      value === String(n)
                        ? `${c.border} ${c.bg} ${c.text}`
                        : 'border-gray-200 text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            ) : (
              <input
                type="number"
                step={test.step}
                max={test.max}
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder={`Ingresá valor en ${test.unit}`}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-xl font-bold focus:outline-none focus:border-emerald-500"
                autoFocus
              />
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-gray-100 flex items-center justify-between gap-2 sticky bottom-0 bg-white">
          <button
            onClick={() => idx > 0 && setIdx(idx - 1)}
            disabled={idx === 0}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 px-3 py-2"
          >
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => save(true)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
            >
              <SkipForward className="w-4 h-4" /> Saltar
            </button>
            {isLast && (results[test.type] || value) ? (
              <button
                onClick={async () => { if (value) await save(); onClose(); }}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg"
              >
                <Trophy className="w-4 h-4" /> Terminar evaluación
              </button>
            ) : (
              <button
                onClick={() => save()}
                disabled={saving || !value}
                className={`flex items-center gap-1.5 ${c.btn} disabled:opacity-40 text-white font-semibold px-5 py-2.5 rounded-lg`}
              >
                {saving ? 'Guardando...' : 'Guardar y siguiente'} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
